import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(quarterOfYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export type HistoricPeriodValue = Dayjs | [Dayjs, Dayjs] | null;

export type HistoricDateRange = {
  dateStart: Dayjs,
  dateEnd: Dayjs,
};

export type DefaultHistoricFilter = {
  periodValue: HistoricPeriodValue,
  dateRange: HistoricDateRange,
};

export function getQuarterBounds (date: Dayjs): HistoricDateRange {
  return {
    dateStart: date.startOf('quarter'),
    dateEnd: date.endOf('quarter'),
  };
}

export function getSemesterBounds (date: Dayjs): HistoricDateRange {
  const isFirstHalf = date.month() < 6;

  if (isFirstHalf) {
    return {
      dateStart: date.startOf('year'),
      dateEnd: date.startOf('year').add(5, 'month').endOf('month'),
    };
  }

  return {
    dateStart: date.startOf('year').add(6, 'month'),
    dateEnd: date.endOf('year'),
  };
}

export function getSemesterAnchor (date: Dayjs): Dayjs {
  return date.month() < 6
    ? date.startOf('year')
    : date.startOf('year').add(6, 'month');
}

export function periodValueToDateRange (
  periodLength: number,
  value: HistoricPeriodValue,
): HistoricDateRange | null {
  if (!value) {
    return null;
  }

  if (periodLength === 1) {
    const [start, end] = value as [Dayjs, Dayjs];
    return { dateStart: start, dateEnd: end };
  }

  if (periodLength === 2) {
    return getSemesterBounds(value as Dayjs);
  }

  return getQuarterBounds(value as Dayjs);
}

function normalizeLeapDay (date: Dayjs): Dayjs {
  if (date.month() === 1 && date.date() === 29) {
    return date.month(2).date(1);
  }

  return date;
}

function buildDateWithYear (year: number, source: Dayjs): Dayjs {
  const month = source.month();
  const day = source.date();
  let candidate = dayjs().year(year).month(month).date(day);

  if (!candidate.isValid() || candidate.month() !== month) {
    candidate = dayjs().year(year).month(2).date(1);
  }

  return normalizeLeapDay(candidate.startOf('day'));
}

export function isDateInsideHistoric (
  date: Dayjs,
  historicStart: Dayjs,
  historicEnd: Dayjs,
): boolean {
  return (
    !date.isBefore(historicStart, 'day') &&
    !date.isAfter(historicEnd, 'day')
  );
}

export function getMatchingPeriod (
  periodStart: Dayjs,
  periodEnd: Dayjs,
  historicStart: Dayjs,
  historicEnd: Dayjs,
): HistoricDateRange | null {
  const histEndYear = historicEnd.year();

  let endMatch = buildDateWithYear(histEndYear, periodEnd);

  if (!isDateInsideHistoric(endMatch, historicStart, historicEnd)) {
    endMatch = buildDateWithYear(histEndYear - 1, periodEnd);

    if (!isDateInsideHistoric(endMatch, historicStart, historicEnd)) {
      return null;
    }
  }

  const yearSpan = periodEnd.year() - periodStart.year();
  const startMatch = buildDateWithYear(endMatch.year() - yearSpan, periodStart);

  if (isDateInsideHistoric(startMatch, historicStart, historicEnd)) {
    return { dateStart: startMatch, dateEnd: endMatch };
  }

  return null;
}

export function getDefaultHistoricFilter (
  periodLength: number,
  periodDateStart: Dayjs,
  periodDateEnd: Dayjs,
  histStart: Dayjs,
  histEnd: Dayjs,
): DefaultHistoricFilter {
  const fullHistoric: HistoricDateRange = {
    dateStart: histStart.startOf('day'),
    dateEnd: histEnd.startOf('day'),
  };

  const matching = getMatchingPeriod(
    periodDateStart.startOf('day'),
    periodDateEnd.startOf('day'),
    fullHistoric.dateStart,
    fullHistoric.dateEnd,
  );

  if (!matching) {
    if (periodLength === 1) {
      return {
        periodValue: [fullHistoric.dateStart, fullHistoric.dateEnd],
        dateRange: fullHistoric,
      };
    }

    return {
      periodValue: null,
      dateRange: fullHistoric,
    };
  }

  if (periodLength === 1) {
    return {
      periodValue: [matching.dateStart, matching.dateEnd],
      dateRange: matching,
    };
  }

  if (periodLength === 2) {
    const anchor = getSemesterAnchor(matching.dateStart);

    return {
      periodValue: anchor,
      dateRange: getSemesterBounds(anchor),
    };
  }

  const anchor = matching.dateStart.startOf('quarter');

  return {
    periodValue: anchor,
    dateRange: getQuarterBounds(anchor),
  };
}

export function isQuarterDisabled (
  current: Dayjs | null,
  histStart: Dayjs,
  histEnd: Dayjs,
): boolean {
  if (!current) {
    return false;
  }

  const { dateStart, dateEnd } = getQuarterBounds(current);

  return !(
    dateStart.isSameOrBefore(histEnd, 'day') &&
    dateEnd.isSameOrAfter(histStart, 'day')
  );
}

export function isSemesterDisabled (
  current: Dayjs | null,
  histStart: Dayjs,
  histEnd: Dayjs,
): boolean {
  if (!current) {
    return false;
  }

  const { dateStart, dateEnd } = getSemesterBounds(current);

  return !(
    dateStart.isSameOrBefore(histEnd, 'day') &&
    dateEnd.isSameOrAfter(histStart, 'day')
  );
}

export function isCustomDateDisabled (
  current: Dayjs | null,
  histStart: Dayjs,
  histEnd: Dayjs,
): boolean {
  if (!current) {
    return false;
  }

  return current.isBefore(histStart, 'day') || current.isAfter(histEnd, 'day');
}

export function isCustomRangeDateDisabled (
  current: Dayjs | null,
  info: { from?: Dayjs },
  histStart: Dayjs,
  histEnd: Dayjs,
  isWeekInterval: boolean,
): boolean {
  if (!current) {
    return false;
  }

  if (isCustomDateDisabled(current, histStart, histEnd)) {
    return true;
  }

  const startDate = info.from;
  if (!startDate) {
    return false;
  }

  if (current.isBefore(startDate, 'day')) {
    return true;
  }

  if (isWeekInterval) {
    if (current.isBefore(startDate.add(7, 'day'), 'day')) {
      return true;
    }
  } else {
    const daysInMonth = startDate.daysInMonth();
    const endOfPeriod = startDate.add(daysInMonth, 'day');

    if (current.isBefore(endOfPeriod, 'day')) {
      return true;
    }
  }

  return false;
}
